import { IconName } from '../ui/icon.component';

export interface NavItem {
  label: string;
  icon: IconName;
  link: string;
  /** Which signed-in state this item belongs to. */
  when: 'always' | 'member' | 'admin' | 'guest';
}

/**
 * The whole navigation, once. The mobile drawer and the desktop bar render this same
 * array, so a link cannot exist in one and be forgotten in the other.
 */
export const NAV: readonly NavItem[] = [
  { label: 'Start', icon: 'home', link: '/start', when: 'always' },
  { label: 'Info', icon: 'info', link: '/info', when: 'always' },
  { label: 'Reserveer', icon: 'calendar', link: '/reserveer', when: 'member' },
  {
    label: 'Mijn reservaties',
    icon: 'clock',
    link: '/mijn-reservaties',
    when: 'member',
  },
  {
    label: 'Mijn gegevens',
    icon: 'user-edit',
    link: '/mijn-gegevens',
    when: 'member',
  },
  { label: 'Overzicht', icon: 'chart-bar', link: '/overzicht', when: 'admin' },
  { label: 'Beheer', icon: 'users', link: '/admin', when: 'admin' },
  {
    label: 'Nieuwe les',
    icon: 'calendar-plus',
    link: '/les-aanmaken',
    when: 'admin',
  },
  {
    label: 'Nieuwe chip',
    icon: 'plus',
    link: '/chip-toevoegen',
    when: 'admin',
  },
  { label: 'Inloggen', icon: 'sign-in', link: '/login', when: 'guest' },
  { label: 'Aanmelden', icon: 'pencil', link: '/aanmelden', when: 'guest' },
];

export function visibleNav(isMember: boolean, isAdmin: boolean): NavItem[] {
  return NAV.filter(
    (item) =>
      item.when === 'always' ||
      (item.when === 'member' && isMember) ||
      (item.when === 'admin' && isMember && isAdmin) ||
      (item.when === 'guest' && !isMember),
  );
}

/**
 * The desktop bar splits the same list three ways, because ten entries in one row need
 * 1296px and a 1280 laptop does not have it:
 *
 *  - `main`  the everyday destinations, always visible
 *  - `admin` the four club-management tools, folded behind one menu
 *  - `auth`  Inloggen / Aanmelden, which are actions rather than places and belong in
 *            the right-hand cluster where a signed-in member sees Uitloggen
 *
 * The mobile drawer keeps rendering `visibleNav` flat, so nothing can appear in one and
 * be missing from the other.
 */
export const isAdminItem = (item: NavItem) => item.when === 'admin';
export const isAuthItem = (item: NavItem) => item.when === 'guest';
export const isMainItem = (item: NavItem) =>
  !isAdminItem(item) && !isAuthItem(item);
